"use client";

import Link from "next/link";
import { ChevronRight, ExternalLink } from "lucide-react";
import { ReactNode } from "react";

interface ModernButtonProps {
  children: ReactNode;
  href?: string;
  variant?: "primary" | "secondary" | "ghost" | "outline" | "purple";
  size?: "sm" | "md" | "lg" | "xl";
  showArrow?: boolean;
  showExternalIcon?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: "button" | "submit" | "reset";
}

export function ModernButton({
  children,
  href,
  variant = "primary",
  size = "lg",
  showArrow = true,
  showExternalIcon = false,
  onClick,
  disabled = false,
  className = "",
  type = "button",
  ...props
}: ModernButtonProps) {
  const baseClasses = "relative inline-flex items-center justify-center font-medium rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed group";
  
  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    md: "px-3 py-2 text-sm",
    lg: "px-4 py-2.5 text-base",
    xl: "px-5 py-3 text-lg"
  };

  const variantClasses = {
    primary: "bg-[color:var(--homepage-primary)] text-white hover:bg-[color:var(--homepage-primary)]/90 shadow-sm hover:shadow-md focus:ring-[color:var(--homepage-primary)]/30",
    secondary: "text-[color:var(--homepage-primary)] hover:text-[color:var(--homepage-primary)]/80 focus:ring-[color:var(--homepage-primary)]/30",
    ghost: "text-[color:var(--homepage-primary)] hover:bg-gray-50 focus:ring-[color:var(--homepage-primary)]/30",
    outline: "border border-gray-300 text-gray-700 hover:border-[color:var(--homepage-primary)] hover:text-[color:var(--homepage-primary)] hover:bg-gray-50 focus:ring-[color:var(--homepage-primary)]/30",
    purple: "bg-[#8c52ff] text-white hover:bg-[#8c52ff]/90 shadow-sm hover:shadow-md focus:ring-[#8c52ff]/30"
  };

  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;

  const iconSize = {
    sm: "w-4 h-4",
    md: "w-5 h-5", 
    lg: "w-5 h-5",
    xl: "w-6 h-6"
  };

  const content = (
    <>
      <span className="relative z-10 flex items-center gap-3">
        {children}
        {showArrow && (
          <ChevronRight className={`${iconSize[size]} transition-transform duration-300 group-hover:translate-x-1 group-hover:scale-110`} />
        )}
        {showExternalIcon && (
          <ExternalLink className={`${iconSize[size]} transition-transform duration-300 group-hover:translate-x-1 group-hover:scale-110`} />
        )}
      </span>
      
      {/* Glassmorphic shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-600 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out"></div>
    </>
  );

  if (href) {
    return (
      <Link href={href} className={buttonClasses} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={buttonClasses}
      {...props}
    >
      {content}
    </button>
  );
}
